/**
 * CanvasTasksPanel — Sidebar-style tasks panel pinned bottom-left on canvas
 *
 * Mirrors the visual of TasksSidebarPanel used in the navigation sidebar footer.
 * Scopes tasks to the current entity (strategy / block) so sibling entities
 * don't leak across canvases. Clicking the fullscreen button opens the
 * TasksViewDialog with the same filter applied.
 *
 * Fullscreen canvas routes (e.g. /project/:pid/strategy/:sid) render outside
 * the layout, so the navigation sidebar's useTaskStoreConnection never runs.
 * We defensively initiate the store connection here via a no-cleanup effect —
 * connect() is idempotent at the store level, so this is safe to call even
 * when the sidebar is also mounted (embedded canvases).
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  TasksSidebarPanel,
  TasksViewDialog,
  useTaskStore,
} from '@modules/background-tasks';
import { TaskPostArtifactsAction } from '@modules/content-plan';
import type { BackendTask, TaskFilter } from '@modules/background-tasks';

interface CanvasTasksPanelProps {
  projectId: string;
  strategyId?: string;
  blockId?: string;
  /** Narrow to a single screen — used inside the V2 screen editor dialog */
  screenId?: string;
  /** Narrow to specific task type(s) — e.g. 'generate-image' */
  taskType?: string | string[];
  /** Override z-index — default 1250 (canvas). Pass 1400 to render above MUI Dialog (1300). */
  zIndex?: number;
}

export const CanvasTasksPanel: React.FC<CanvasTasksPanelProps> = ({
  projectId,
  strategyId,
  blockId,
  screenId,
  taskType,
  zIndex = 1250,
}) => {
  const [fullscreen, setFullscreen] = useState(false);
  const renderTaskResultActions = React.useCallback(
    (task: BackendTask) => <TaskPostArtifactsAction task={task} />,
    [],
  );
  const renderTaskRowActions = React.useCallback(
    (task: BackendTask) => <TaskPostArtifactsAction task={task} variant="icon" />,
    [],
  );

  // Ensure the task store is connected to this project. Called here (rather
  // than via useTaskStoreConnection with its cleanup) so that unmounting the
  // canvas doesn't tear down a listener the sidebar still needs in embedded
  // contexts. connect() is idempotent — same project is a no-op.
  //
  // Auth is async: on fullscreen routes the canvas mounts before
  // getAuth().currentUser is populated, so we subscribe to auth state and
  // (re)connect as soon as a user is available.
  useEffect(() => {
    if (!projectId) return;
    const auth = getAuth();
    const tryConnect = (uid?: string | null) => {
      if (uid) useTaskStore.getState().connect(projectId, uid);
    };
    // First attempt with whatever auth already has
    tryConnect(auth.currentUser?.uid);
    // Keep listening so a late sign-in still triggers connection
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      tryConnect(user?.uid);
    });
    return () => unsubscribe();
  }, [projectId]);

  const filter = useMemo<TaskFilter>(
    () => ({
      ...(strategyId ? { strategyId } : {}),
      ...(blockId ? { blockId } : {}),
      ...(screenId ? { screenId } : {}),
      ...(taskType ? { taskType } : {}),
      ownerMode: 'all',
    }),
    [strategyId, blockId, screenId, taskType]
  );

  return (
    <>
      <Box
        data-canvas-tasks-panel
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          zIndex,
          width: 320,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          pointerEvents: 'auto',
        }}
      >
        <TasksSidebarPanel
          collapsed={false}
          filter={filter}
          hideWhenEmpty={false}
          onOpenFullscreen={() => setFullscreen(true)}
          renderResultActions={renderTaskResultActions}
          renderRowActions={renderTaskRowActions}
        />
      </Box>
      <TasksViewDialog
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        filter={filter}
        renderResultActions={renderTaskResultActions}
        renderRowActions={renderTaskRowActions}
      />
    </>
  );
};

export default CanvasTasksPanel;
